import React, { useState, useEffect, useMemo } from 'react';
import { ethers } from 'ethers';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ShoppingCart,
  AccountBalanceWallet,
  CheckCircle,
  HourglassEmpty,
  Error as ErrorIcon,
  Launch,
  ContentCopy,
  VpnKey,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import { useWallet } from '../hooks/useWallet';
import { useThemeMode } from '../hooks/useThemeMode';
import { useWallet as useSolWallet, useConnection } from '@solana/wallet-adapter-react';
import { Connection } from '@solana/web3.js';
import depositTracker from '../services/depositTracker';
import multiTokenTreasuryService from '../services/multiTokenTreasuryService';
import solanaInternullService from '../services/solanaInternullService';
import tronInternullService from '../services/tronInternullService';
import { useTronWallet } from '../hooks/useTronWallet';
import { getChainLogoUrl } from '../components/ChainLogos';
import { getMetaMaskProvider } from '../utils/contractInteraction';

// Contract addresses and supported chains are now loaded dynamically from backend
// via multiTokenTreasuryService.loadChainsFromBackend()

const Deposit = () => {
  const navigate = useNavigate();
  const { connected, account, chainId, signer, provider, connectWallet } = useWallet();
  const { publicKey: solanaPublicKey, connected: solanaConnected, sendTransaction: solanaSendTransaction, signTransaction: solanaSignTransaction } = useSolWallet();
  const { connection: solanaConnection } = useConnection();
  // Tron wallet (TronLink) — used ONLY when the selected chain is Tron.
  const { address: tronAddress, connected: tronConnected, tronWeb, connect: connectTron } = useTronWallet();
  const { isDark } = useThemeMode();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [selectedChainId, setSelectedChainId] = useState(''); // No default selection
  const [tokens, setTokens] = useState([]);
  const [selectedToken, setSelectedToken] = useState('');
  const [selectedDenomination, setSelectedDenomination] = useState('');
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reservation, setReservation] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [txHash, setTxHash] = useState('');
  const [depositDialog, setDepositDialog] = useState(false);
  const [supportedChains, setSupportedChains] = useState([]);
  const [targetChain, setTargetChain] = useState('');
  const [crossChainMode, setCrossChainMode] = useState(false);
  const [walletBalance, setWalletBalance] = useState(null);
  const [depositProgressDialog, setDepositProgressDialog] = useState(false);
  const [depositStatus, setDepositStatus] = useState(''); // 'pending', 'confirming', 'confirmed', 'failed'

  // Store completed deposit info for display
  const [completedDeposit, setCompletedDeposit] = useState(null);

  // Compute minimum deposit from the selected token's supported denominations.
  // Backend stores denominations as strings — they may be in human-readable token
  // units ("1" = 1 USDC) or in the token's smallest unit ("1000000" = 1 USDC with
  // 6 decimals). Heuristic: if the parsed value >= 10^decimals it's in raw units
  // and must be divided; otherwise it's already a human-readable amount.
  const minDeposit = useMemo(() => {
    const token = tokens.find(t => t.address === selectedToken);
    if (!token?.denominations?.length) return 0.001; // fallback
    const threshold = Math.pow(10, token.decimals ?? 18);
    const inTokens = token.denominations.map(d => {
      const n = parseFloat(d);
      return n >= threshold ? n / threshold : n;
    });
    return Math.min(...inTokens);
  }, [tokens, selectedToken]);

  const steps = crossChainMode
    ? ['Select Token & Chain', 'Lock Funds', 'Claim Cross-Chain Keys']
    : ['Select Token & Amount', 'Lock Funds', 'Verify & Claim Keys'];

  // Load chains on mount - no wallet needed
  useEffect(() => {
    const loadChainsOnMount = async () => {
      try {
        await multiTokenTreasuryService.loadChainsFromBackend();
        await loadChains();
      } catch (error) {
        console.error('Failed to load chains:', error);
        toast.error('Failed to load chains. Please refresh the page.');
      }
    };

    loadChainsOnMount();
  }, []); // Run once on mount

  const loadTokens = async (chainId) => {
    // Use the passed chainId parameter instead of state to avoid async state issues
    const targetChainId = chainId || selectedChainId;
    console.log('🔗 [Purchase.js] Loading supported tokens for chain:', targetChainId, typeof targetChainId);

    try {
      const supportedTokens = await multiTokenTreasuryService.getSupportedTokens(targetChainId);
      console.log('📋 [Purchase.js] Received tokens from service:', supportedTokens);

      setTokens(supportedTokens);

      // Auto-select native token based on chain
      const chainInfo = supportedChains.find(c => c.chainId === targetChainId);
      console.log('🔍 [Purchase.js] Chain info:', chainInfo);
      const nativeSymbol = chainInfo?.nativeCurrency || chainInfo?.symbol || 'ETH';
      const defaultToken = supportedTokens.find(t =>
        t.symbol === nativeSymbol ||
        t.isNative ||
        t.address === '0x0000000000000000000000000000000000000000' ||
        t.address === '11111111111111111111111111111111'
      );

      console.log('🔍 [Purchase.js] Looking for default native token:', nativeSymbol, 'Found:', defaultToken);

      if (defaultToken) {
        setSelectedToken(defaultToken.address);
        console.log('✅ [Purchase.js] Auto-selected token:', defaultToken.symbol);
      } else if (supportedTokens.length > 0) {
        // If no native token found, select the first token
        setSelectedToken(supportedTokens[0].address);
        console.log('✅ [Purchase.js] Auto-selected first token:', supportedTokens[0].symbol);
      }

      console.log('✅ [Purchase.js] Tokens loaded and set:', supportedTokens.map(t => t.symbol));
    } catch (error) {
      console.error('[Purchase.js] Failed to load tokens:', error);
      // Fallback to native token based on selected chain
      const chainInfo = supportedChains.find(c => c.chainId === targetChainId);
      const fallbackSymbol = chainInfo?.nativeCurrency || chainInfo?.symbol || 'ETH';
      const fallbackName = fallbackSymbol === 'BNB' || fallbackSymbol === 'tBNB' ? 'Binance Coin' :
        fallbackSymbol === 'SOL' ? 'Solana' : 'Ethereum';

      console.log('⚠️ [Purchase.js] Using fallback token:', fallbackSymbol);

      const fallbackTokens = [{
        address: fallbackSymbol === 'SOL' ? '11111111111111111111111111111111' : '0x0000000000000000000000000000000000000000',
        symbol: fallbackSymbol,
        name: fallbackName,
        decimals: fallbackSymbol === 'SOL' ? 9 : 18,
        chainId: targetChainId,
        isNative: true,
        denominations: [0.001, 0.01, 0.1, 0.5, 1.0]
      }];
      setTokens(fallbackTokens);
      setSelectedToken(fallbackTokens[0].address);

      console.log('✅ [Purchase.js] Fallback token set:', fallbackTokens[0]);
    }
  };

  const loadChains = async () => {
    const chains = await multiTokenTreasuryService.getSupportedChains();
    console.log('📋 Loaded chains from backend:', chains);

    // Format chains for the UI
    const formattedChains = chains.map(chain => ({
      chainId: chain.chainId,
      name: chain.name,
      symbol: chain.nativeCurrency || 'ETH',
      treasuryAddress: chain.treasuryAddress,
      chainType: chain.chainType || 'evm'
    }));

    setSupportedChains(formattedChains);
    // Don't set a default chain - user must select one
  };

  const loadSolanaTokens = async (chainId) => {
    const targetChainId = chainId || selectedChainId;
    console.log('🌟 [Purchase.js] Loading Solana tokens for chain:', targetChainId);

    try {
      const supportedTokens = await solanaInternullService.getSupportedTokens();
      console.log('📋 [Purchase.js] Received Solana tokens:', supportedTokens);

      setTokens(supportedTokens);

      // Auto-select SOL as default
      const defaultToken = supportedTokens.find(t => t.symbol === 'SOL' || t.isNative);

      if (defaultToken) {
        setSelectedToken(defaultToken.address);
        console.log('✅ [Purchase.js] Auto-selected Solana token:', defaultToken.symbol);
      }

      console.log('✅ [Purchase.js] Solana tokens loaded:', supportedTokens.map(t => t.symbol));
    } catch (error) {
      console.error('[Purchase.js] Failed to load Solana tokens:', error);
      // Fallback to default SOL token
      const fallbackTokens = [{
        address: '11111111111111111111111111111111',
        symbol: 'SOL',
        name: 'Solana',
        decimals: 9,
        chainId: targetChainId,
        isNative: true,
        denominations: [0.1, 1, 10]
      }];
      setTokens(fallbackTokens);
      setSelectedToken(fallbackTokens[0].address);

      console.log('✅ [Purchase.js] Fallback Solana token set:', fallbackTokens[0]);
    }
  };

  const handleRequestKey = async () => {
    if (!selectedChainId) {
      toast.error('Please select a network first');
      return;
    }

    if (!selectedDenomination) {
      toast.error('Please enter a deposit amount');
      return;
    }

    const depositAmount = parseFloat(selectedDenomination);
    if (isNaN(depositAmount) || depositAmount <= 0) {
      toast.error('Please enter a valid deposit amount');
      return;
    }

    // Detect chain type. Three-way routing enforces the privacy invariant:
    // each chain ALWAYS goes through its own wallet provider, never falling
    // back to "the first connected wallet."
    const selectedChain = supportedChains.find(c => c.chainId === selectedChainId);
    const isSolana = selectedChain?.chainType === 'solana' ||
      selectedChain?.name?.toLowerCase().includes('solana') ||
      (typeof selectedChainId === 'string' && selectedChainId.toLowerCase().includes('solana'));
    const isTron = selectedChain?.chainType === 'tron' ||
      selectedChain?.name?.toLowerCase().startsWith('tron') ||
      (typeof selectedChainId === 'string' && selectedChainId.toString().toLowerCase().startsWith('tron'));

    try {
      setLoading(true);

      // Get the selected token
      const token = tokens.find(t => t.address === selectedToken);
      if (!token) {
        toast.error('Please select a token');
        setLoading(false);
        return;
      }

      // Connect appropriate wallet based on chain type. We NEVER touch the
      // wrong wallet — e.g. selecting a Tron chain must not trigger MetaMask.
      if (isSolana) {
        if (!solanaConnected || !solanaPublicKey) {
          toast.info('Please connect your Phantom wallet using the button in the header');
          setLoading(false);
          return;
        }
        console.log('✅ Solana wallet connected:', solanaPublicKey.toString());
      } else if (isTron) {
        // Tron chain — TronLink only. Never touch window.ethereum here.
        if (!tronConnected || !tronAddress) {
          toast.info('Connecting to TronLink...');
          try {
            await connectTron();
          } catch (err) {
            toast.error(err.message || 'Failed to connect TronLink');
            setLoading(false);
            return;
          }
        }
        console.log('✅ TronLink connected:', tronAddress || '(awaiting state update)');
      } else {
        // EVM chain - connect MetaMask if not connected
        if (!connected || !account) {
          toast.info('Connecting to MetaMask...');
          await connectWallet(); // This will trigger MetaMask popup

          // Wait a moment for state to update
          await new Promise(resolve => setTimeout(resolve, 1000));

          // EVM policy: resolve the GENUINE MetaMask via EIP-6963 — never raw
          // window.ethereum, which another installed wallet (Phantom/TronLink)
          // can hijack. This keeps the deposit on MetaMask so it goes out as a
          // direct transaction from the user's address.
          const metamaskProvider = getMetaMaskProvider();

          if (!metamaskProvider || !metamaskProvider.selectedAddress) {
            toast.error('Failed to connect MetaMask. Please try again.');
            setLoading(false);
            return;
          }

          console.log('✅ MetaMask connected:', metamaskProvider.selectedAddress);
        } else {
          console.log('✅ MetaMask already connected:', account);
        }
      }

      // Get minimum denomination from token configuration.
      // Same heuristic as minDeposit useMemo: if value >= 10^decimals it's in raw
      // units and must be divided; otherwise it's already in human-readable form.
      let minimumAmount = 0.001; // fallback
      if (token.denominations && token.denominations.length > 0) {
        const threshold = Math.pow(10, token.decimals ?? 18);
        const denominationsInTokens = token.denominations.map(d => {
          const n = parseFloat(d);
          return n >= threshold ? n / threshold : n;
        });
        minimumAmount = Math.min(...denominationsInTokens);
      } else {
        minimumAmount = token.isNative ? 0.001 : 0.1;
      }

      if (depositAmount < minimumAmount) {
        toast.error(`Minimum deposit: ${minimumAmount} ${token.symbol}`);
        setLoading(false);
        return;
      }

      console.log('🎯 Creating reservation:', {
        token: token.symbol,
        amount: depositAmount,
        crossChain: crossChainMode,
        targetChain: targetChain
      });

      // Get the correct treasury address for the selected chain
      const treasuryAddress = multiTokenTreasuryService.getContractAddressForChain(selectedChainId);
      console.log('🏦 Treasury address for chain', selectedChainId, ':', treasuryAddress);

      // Create reservation
      const reservationData = {
        reservation_id: `res_${Date.now()}`,
        amount: depositAmount,
        token: token,
        tokenAddress: token.address,
        expiry: Date.now() + (15 * 60 * 1000),
        crossChain: crossChainMode,
        sourceChain: chainId,
        targetChain: crossChainMode ? targetChain : chainId,
        deposit_address: treasuryAddress || multiTokenTreasuryService.contractAddress
      };

      setReservation(reservationData);
      setActiveStep(1);
      console.log('✅ Reservation created:', reservationData);

    } catch (error) {
      console.error('Request key error:', error);
      toast.error(error.message || 'Failed to create reservation');
    } finally {
      setLoading(false);
    }
  };

  // Add chain to MetaMask
  const handleAddChainToMetaMask = async (chainParams) => {
    const mm = getMetaMaskProvider();
    if (!mm) {
      toast.error('MetaMask not detected. Please install MetaMask to add networks.');
      return;
    }

    try {
      await mm.request({
        method: 'wallet_addEthereumChain',
        params: [chainParams],
      });
      toast.success(`${chainParams.chainName} added to MetaMask successfully!`);
    } catch (error) {
      console.error('Error adding chain to MetaMask:', error);
      if (error.code === 4001) {
        toast.error('User rejected the request');
      } else {
        toast.error(`Failed to add ${chainParams.chainName}: ${error.message}`);
      }
    }
  };

  const handleDeposit = () => {
    setDepositDialog(true);
  };

  // Execute a Solana deposit and handle confirmation
  const executeSolanaDeposit = async () => {
    if (!solanaConnected || !solanaPublicKey) {
      throw new Error('Solana wallet not connected. Please connect your Phantom wallet.');
    }

    const depositAmount = reservation.amount;
    const token = reservation.token;
    const isNativeSOL = token.isNative || token.symbol === 'SOL';

    console.log('💰 Depositing:', depositAmount, token.symbol);

    // Use backend RPC for better performance if available
    let connectionToUse = solanaConnection;
    const backendRpcUrl = multiTokenTreasuryService.getRpcUrlByChainName('solana-devnet');
    if (backendRpcUrl) {
      console.log('🔗 Using backend RPC for Solana deposit:', backendRpcUrl);
      connectionToUse = new Connection(backendRpcUrl, 'confirmed');
    }

    await solanaInternullService.initialize(connectionToUse, {
      publicKey: solanaPublicKey,
      sendTransaction: solanaSendTransaction,
      signTransaction: solanaSignTransaction,
    });

    // Execute deposit based on token type
    let result;
    if (isNativeSOL) {
      result = await solanaInternullService.depositNative(depositAmount);
    } else {
      result = await solanaInternullService.depositToken(depositAmount, token.address, token.decimals);
    }

    if (!result || !result.success) {
      throw new Error('Solana deposit failed');
    }

    setTxHash(result.txHash);
    setDepositStatus('confirming');

    // Track the deposit
    depositTracker.addDeposit(
      result.txHash, reservation.amount, solanaPublicKey.toString(), 'solana-devnet',
      { token: token.symbol, tokenAddress: isNativeSOL ? 'native' : token.address, crossChain: reservation.crossChain, targetChain: reservation.targetChain }
    );
    toast.success('Solana transaction submitted!');

    // Confirmation already handled inside solanaInternullService (polling-based, no WebSocket)
    setDepositStatus('confirmed');
    toast.success('Solana deposit confirmed!');

    // Store completed deposit info for key request
    setCompletedDeposit({
      txHash: result.txHash, amount: reservation.amount,
      userAddress: solanaPublicKey.toString(), chainId: selectedChainId, token: token
    });

    setTimeout(() => { setDepositProgressDialog(false); setActiveStep(2); }, 2000);
  };

  // Tron deposit — native TRX via TronLink. Never touches MetaMask or Phantom.
  // Mirrors executeSolanaDeposit but uses the TronWeb instance from useTronWallet.
  const executeTronDeposit = async () => {
    if (!tronConnected || !tronAddress || !tronWeb) {
      throw new Error('TronLink not connected. Click "Connect TronLink" in the wallet menu.');
    }

    const depositAmount = reservation.amount;
    const token = reservation.token;
    const isNativeTRX = token.isNative || token.symbol === 'TRX' || (token.address === '0x0000000000000000000000000000000000000000');

    const selectedChain = supportedChains.find(c => c.chainId === selectedChainId);
    const treasuryAddress = selectedChain?.treasuryAddress;
    if (!treasuryAddress) {
      throw new Error('Treasury address not configured for this Tron chain in the backend.');
    }

    let result;
    if (isNativeTRX) {
      console.log('💰 Depositing on Tron:', depositAmount, 'TRX to', treasuryAddress);
      result = await tronInternullService.depositNative(tronWeb, treasuryAddress, depositAmount);
    } else {
      // TRC-20 path: approve treasury then call deposit(token, amount).
      const tokenAddress = token.address;
      const decimals = token.decimals;
      if (!tokenAddress || typeof tokenAddress !== 'string' || !tokenAddress.startsWith('T')) {
        throw new Error(`Invalid TRC-20 token address for ${token.symbol}: ${tokenAddress}`);
      }
      if (decimals === undefined || decimals === null) {
        throw new Error(`Decimals missing for ${token.symbol} — backend chain config out of sync.`);
      }
      console.log(`💰 Depositing on Tron: ${depositAmount} ${token.symbol} (${decimals} dec) to ${treasuryAddress} via TRC-20 ${tokenAddress}`);
      result = await tronInternullService.depositToken({
        tronWeb,
        treasuryAddress,
        tokenAddress,
        amount: depositAmount,
        decimals,
      });
    }
    if (!result || !result.success) throw new Error('Tron deposit failed');

    setTxHash(result.txHash);
    setDepositStatus('confirming');

    // Track the deposit. Chain ID for Tron Nile is the numeric Tron chain ID.
    const chainKey = typeof selectedChainId === 'number' ? selectedChainId : selectedChainId;
    depositTracker.addDeposit(
      result.txHash, reservation.amount, tronAddress, chainKey,
      {
        token: token.symbol,
        tokenAddress: isNativeTRX ? 'native' : token.address,
        crossChain: reservation.crossChain,
        targetChain: reservation.targetChain,
      },
    );
    toast.success('Tron transaction submitted!');

    // Tron blocks confirm in ~3s but the DKG verifier requires 20 confirmations,
    // so we mark "confirmed" optimistically here and let the keyshare-request
    // step block until the backend sees enough confirmations. CRITICAL: also
    // update the persisted depositTracker status — without this, the deposit
    // stays as 'pending' in localStorage and the Claim Keys tab filters it out.
    depositTracker.updateDepositStatus(result.txHash, 'confirmed');
    setDepositStatus('confirmed');
    toast.success('Tron deposit confirmed!');

    setCompletedDeposit({
      txHash: result.txHash, amount: reservation.amount,
      userAddress: tronAddress, chainId: chainKey, token,
    });

    setTimeout(() => { setDepositProgressDialog(false); setActiveStep(2); }, 2000);
  };

  // Ensure EVM wallet is on the correct network, switching or adding chain if needed.
  // Initializes the treasury service and returns when ready.
  const ensureEvmNetwork = async () => {
    if (chainId === selectedChainId) {
      // Already on correct network
      const treasuryAddress = multiTokenTreasuryService.getContractAddressForChain(selectedChainId);
      if (!treasuryAddress) throw new Error(`Treasury contract not configured for chain ${selectedChainId}`);
      await multiTokenTreasuryService.initialize(provider, signer, treasuryAddress);
      return;
    }

    console.log(`⚠️ Network mismatch! Switching from ${chainId} to ${selectedChainId}...`);

    const metamaskProvider = getMetaMaskProvider();
    if (!metamaskProvider) {
      throw new Error('MetaMask not found. Please install MetaMask to deposit on EVM chains.');
    }

    // Helper: wait for MetaMask to actually report the target chain, then init
    // treasury. Polls eth_chainId (up to ~12s) instead of a fixed 2s delay plus
    // a single read — the old approach raced with MetaMask's switch and falsely
    // reported "Network switch failed" whenever the switch took longer than 2s
    // (slow wallet, chain just added, or a second wallet extension present).
    const refreshAndInitTreasury = async (mmProvider) => {
      const readChainId = async () => {
        try {
          return parseInt(await mmProvider.request({ method: 'eth_chainId' }), 16);
        } catch (_) {
          try {
            return (await new ethers.providers.Web3Provider(mmProvider).getNetwork()).chainId;
          } catch (_) {
            return null;
          }
        }
      };

      let newChainId = await readChainId();
      for (let i = 0; i < 40 && newChainId !== selectedChainId; i++) {
        await new Promise(resolve => setTimeout(resolve, 300));
        newChainId = await readChainId();
      }
      console.log(`✅ Now on network ${newChainId}`);

      const newProvider = new ethers.providers.Web3Provider(mmProvider);
      const newSigner = newProvider.getSigner();

      const treasuryAddress = multiTokenTreasuryService.getContractAddressForChain(selectedChainId);
      if (!treasuryAddress) throw new Error(`Treasury contract not configured for chain ${selectedChainId}`);
      await multiTokenTreasuryService.initialize(newProvider, newSigner, treasuryAddress);
      return newChainId;
    };

    try {
      await metamaskProvider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${selectedChainId.toString(16)}` }],
      });

      let newChainId = await refreshAndInitTreasury(metamaskProvider);
      if (newChainId !== selectedChainId) {
        // The chain can transiently revert (e.g. a second wallet extension
        // re-emitting its own network). Request the switch once more and re-poll
        // before giving up.
        await metamaskProvider.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: `0x${selectedChainId.toString(16)}` }],
        });
        newChainId = await refreshAndInitTreasury(metamaskProvider);
      }
      if (newChainId !== selectedChainId) {
        throw new Error(`Network switch failed. Expected ${selectedChainId}, got ${newChainId}`);
      }
    } catch (switchError) {
      console.error('Network switch error:', switchError);

      // RPC and explorer fallback URLs
      const rpcUrls = {
        97: ['https://bsc-testnet-rpc.publicnode.com', 'https://bsc-testnet.public.blastapi.io', 'https://data-seed-prebsc-1-s1.binance.org:8545', 'https://data-seed-prebsc-2-s1.binance.org:8545'],
        56: ['https://bsc-dataseed.binance.org', 'https://bsc-rpc.publicnode.com'],
        11155111: ['https://ethereum-sepolia-rpc.publicnode.com', 'https://rpc.sepolia.org'],
        1: ['https://ethereum-rpc.publicnode.com', 'https://rpc.ankr.com/eth'],
        137: ['https://polygon-rpc.com', 'https://rpc.ankr.com/polygon'],
        80001: ['https://rpc-mumbai.maticvigil.com'],
        84532: ['https://sepolia.base.org'],
        8453: ['https://mainnet.base.org'],
        998: ['https://api.hyperliquid-testnet.xyz/evm'],
        999: ['https://api.hyperliquid.xyz/evm'],
      };
      const explorers = {
        97: ['https://testnet.bscscan.com'], 56: ['https://bscscan.com'],
        11155111: ['https://sepolia.etherscan.io'], 1: ['https://etherscan.io'],
        137: ['https://polygonscan.com'], 80001: ['https://mumbai.polygonscan.com'],
        84532: ['https://sepolia.basescan.org'], 8453: ['https://basescan.org'],
        998: ['https://explorer.hyperliquid-testnet.xyz'], 999: ['https://explorer.hyperliquid.xyz'],
      };

      if (switchError.code === 4902 || switchError.message?.includes('not connected to the requested chain')) {
        const chainInfo = supportedChains.find(c => c.chainId === selectedChainId);
        if (!chainInfo) throw new Error(`Chain ${selectedChainId} not found in configuration`);

        try {
          console.log(`📝 Adding ${chainInfo.name} to MetaMask...`);
          await metamaskProvider.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: `0x${selectedChainId.toString(16)}`,
              chainName: chainInfo.name,
              nativeCurrency: { name: chainInfo.symbol, symbol: chainInfo.symbol, decimals: chainInfo.decimals || 18 },
              rpcUrls: rpcUrls[selectedChainId] || [],
              blockExplorerUrls: explorers[selectedChainId] || []
            }],
          });
          await refreshAndInitTreasury(metamaskProvider);
        } catch (addError) {
          console.error('Failed to add chain:', addError);
          throw new Error(`Please add ${chainInfo.name} to MetaMask manually and try again`);
        }
      } else if (switchError.code === 4001) {
        throw new Error('Network switch rejected by user');
      } else {
        throw new Error(`Failed to switch to ${supportedChains.find(c => c.chainId === selectedChainId)?.name || selectedChainId}. Please switch manually in MetaMask.`);
      }
    }
  };

  const executeDeposit = async () => {
    if (!reservation) return;

    try {
      setLoading(true);
      setDepositDialog(false);
      setDepositProgressDialog(true);
      setDepositStatus('pending');
      console.log('💰 Executing deposit:', reservation);

      // Detect chain type — three-way for privacy isolation.
      const selectedChain = supportedChains.find(c => c.chainId === selectedChainId);
      const isSolana = selectedChain?.chainType === 'solana' ||
        selectedChain?.name?.toLowerCase().includes('solana') ||
        (typeof selectedChainId === 'string' && selectedChainId.toLowerCase().includes('solana'));
      const isTron = selectedChain?.chainType === 'tron' ||
        selectedChain?.name?.toLowerCase().startsWith('tron') ||
        (typeof selectedChainId === 'string' && selectedChainId.toString().toLowerCase().startsWith('tron'));

      if (isSolana) {
        await executeSolanaDeposit();
      } else if (isTron) {
        await executeTronDeposit();
      } else {
        // EVM deposit
        if (!signer) throw new Error('EVM wallet not connected');
        if (!connected || !account) throw new Error('Wallet not connected');

        const depositAmount = reservation.amount;
        console.log('💰 Depositing:', depositAmount, reservation.tokenAddress === '0x0000000000000000000000000000000000000000' ? 'ETH' : 'tokens');

        // Ensure correct network and initialize treasury service
        await ensureEvmNetwork();

        // Check wallet balance
        if (walletBalance !== null && depositAmount > walletBalance) {
          throw new Error(`Insufficient balance. You have ${walletBalance.toFixed(4)} ${reservation.token.symbol} but trying to deposit ${depositAmount} ${reservation.token.symbol}`);
        }

        // Execute the deposit
        console.log('📝 Calling deposit function...');
        const result = await multiTokenTreasuryService.deposit(reservation.tokenAddress, depositAmount);
        console.log('💰 Deposit result:', result);

        if (!result || !result.success) throw new Error('Deposit failed');

        setTxHash(result.txHash);
        setDepositStatus('confirmed');
        console.log('💾 Saving deposit with chainId:', selectedChainId);

        // Track the deposit as confirmed
        depositTracker.addDeposit(
          result.txHash, reservation.amount, account, selectedChainId,
          { token: reservation.token.symbol, tokenAddress: reservation.tokenAddress, crossChain: reservation.crossChain, targetChain: reservation.targetChain || selectedChainId }
        );
        depositTracker.updateDepositStatus(result.txHash, 'confirmed');

        setCompletedDeposit({
          txHash: result.txHash, amount: reservation.amount,
          userAddress: account, chainId: selectedChainId, token: reservation.token
        });

        toast.success(`Deposit successful! Transaction: ${result.txHash.slice(0, 10)}...`);
        setTimeout(() => { setDepositProgressDialog(false); setActiveStep(2); }, 2000);
      }
    } catch (error) {
      console.error('❌ Deposit error:', error);
      console.error('❌ Error details:', { stack: error.stack, reason: error.reason, data: error.data });

      setDepositStatus('failed');
      toast.error(error.message || 'Deposit failed');
      setTimeout(() => { setDepositProgressDialog(false); }, 3000);
    } finally {
      setLoading(false);
    }
  };

  const getExplorerUrl = (txHash) => {
    if (!txHash) return '';

    // Use the selected deposit chain, not the wallet's current chain
    const selectedChain = supportedChains.find(c => c.chainId === selectedChainId);

    // Detect chain type for explorer URL routing.
    const isSolana = selectedChain?.chainType === 'solana' ||
      selectedChain?.name?.toLowerCase().includes('solana') ||
      (typeof selectedChainId === 'string' && selectedChainId.toLowerCase().includes('solana'));
    const isTron = selectedChain?.chainType === 'tron' ||
      selectedChain?.name?.toLowerCase().startsWith('tron');

    if (isSolana) {
      if (selectedChainId?.includes('mainnet')) {
        return `https://explorer.solana.com/tx/${txHash}`;
      }
      return `https://explorer.solana.com/tx/${txHash}?cluster=devnet`;
    }
    if (isTron) {
      return tronInternullService.explorerUrl(selectedChain?.name, txHash);
    }

    // Use backend-provided block explorer if available
    if (selectedChain?.blockExplorer) {
      const base = selectedChain.blockExplorer.replace(/\/+$/, '');
      return `${base}/tx/${txHash}`;
    }

    // Fallback: use selectedChainId (deposit chain), not wallet chainId
    const depositChain = typeof selectedChainId === 'number' ? selectedChainId : parseInt(selectedChainId);
    const explorerMap = {
      11155111: 'https://sepolia.etherscan.io',
      1: 'https://etherscan.io',
      97: 'https://testnet.bscscan.com',
      56: 'https://bscscan.com',
      80002: 'https://amoy.polygonscan.com',
      137: 'https://polygonscan.com',
      84532: 'https://sepolia.basescan.org',
      8453: 'https://basescan.org',
      998: 'https://explorer.hyperliquid-testnet.xyz',
      999: 'https://explorer.hyperliquid.xyz',
    };
    const base = explorerMap[depositChain] || 'https://etherscan.io';
    return `${base}/tx/${txHash}`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };


  const formatTimeRemaining = (expiryTime) => {
    if (!expiryTime) return '';

    const expiry = new Date(expiryTime);
    const now = new Date();
    const diff = expiry.getTime() - now.getTime();

    if (diff <= 0) return 'Expired';

    const minutes = Math.floor(diff / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleChainChange = async (event) => {
    const rawValue = event.target.value;
    // Keep as string if it's a string chain ID (Solana), otherwise convert to number (EVM)
    const newChainId = isNaN(rawValue) || (typeof rawValue === 'string' && rawValue.includes('-')) ? rawValue : Number(rawValue);

    setSelectedChainId(newChainId);
    setTargetChain(newChainId);

    // Reset form when changing chains
    setSelectedToken('');
    setSelectedDenomination('');
    setAvailability(null);
    setReservation(null);
    setActiveStep(0);
    setTokens([]);

    // Detect chain type (three-way, for routing — not for wallet popups here).
    const selectedChain = supportedChains.find(c => c.chainId === newChainId);
    const isSolana = selectedChain?.chainType === 'solana' ||
      selectedChain?.name?.toLowerCase().includes('solana') ||
      (typeof newChainId === 'string' && newChainId.toLowerCase().includes('solana'));
    const isTron = selectedChain?.chainType === 'tron' ||
      selectedChain?.name?.toLowerCase().startsWith('tron');

    console.log(`Chain selected: ${newChainId} (${isSolana ? 'Solana' : isTron ? 'Tron' : 'EVM'})`);

    // Load tokens for the selected chain immediately (without wallet connection)
    // IMPORTANT: Pass newChainId as parameter to avoid async state issues
    try {
      setLoading(true);
      if (isSolana) {
        console.log('🌟 Loading Solana tokens...');
        await loadSolanaTokens(newChainId);
      } else {
        // EVM and Tron both use the chain-agnostic backend token endpoint.
        // The chain config already has TRX registered for tron-nile.
        console.log(isTron ? '🟥 Loading Tron tokens...' : '🔗 Loading EVM tokens...');
        await loadTokens(newChainId);
      }
      console.log('✅ Tokens loaded for chain:', newChainId);
    } catch (error) {
      console.error('Failed to load tokens for chain:', error);
      toast.error('Failed to load tokens for this chain');
    } finally {
      setLoading(false);
    }
  };

  // Remove the wallet connection gate - allow users to see chains without connecting wallet first

  // Chain logos mapping - match exact backend names
  const getChainLogo = getChainLogoUrl;

  const accent = '#22c55e';
  const subtleText = isDark ? '#a3a3a3' : '#525252';
  const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';

  return (
    <Box>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { md: 'flex-end' },
        justifyContent: 'space-between',
        gap: 2,
        borderBottom: `1px solid ${borderColor}`,
        pb: 3,
        mb: 3,
      }}>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 900,
              letterSpacing: '-0.03em',
              color: isDark ? '#e1e2eb' : '#171717',
              mb: 0.5,
              fontSize: { xs: '1.8rem', md: '2.4rem' },
            }}
          >
            Lock Funds
          </Typography>
          <Typography sx={{ color: subtleText, fontSize: '0.9rem' }}>
            Deposit tokens into the treasury to generate privacy keys
          </Typography>
        </Box>
      </Box>

      {/* Stepper */}
      <Stepper activeStep={activeStep} alternativeLabel={isMobile} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step 1: Select Network, Token & Amount */}
      {activeStep === 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 340px' }, gap: 3 }}>
          {/* Left — Form */}
          <Card variant="outlined" sx={{ border: `1px solid ${borderColor}`, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#fff' }}>
            <CardContent sx={{ p: 3 }}>
              {/* Network selector */}
              <Typography variant="caption" sx={{ fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: '0.15em', display: 'block', mb: 1.5 }}>
                Select Network
              </Typography>
              {supportedChains.length === 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" sx={{ color: subtleText }}>Loading networks...</Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                  {supportedChains.map((chain) => {
                    const logoSrc = getChainLogo(chain.name);
                    const isSelected = selectedChainId === chain.chainId;
                    return (
                      <Box
                        key={chain.chainId}
                        onClick={() => handleChainChange({ target: { value: chain.chainId } })}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1,
                          px: 1.5, py: 1,
                          cursor: 'pointer',
                          border: `1px solid ${isSelected ? accent : (isDark ? '#262626' : '#e5e5e5')}`,
                          bgcolor: isSelected ? (isDark ? 'rgba(34,197,94,0.08)' : 'rgba(34,197,94,0.06)') : 'transparent',
                          transition: 'all 0.15s',
                          '&:hover': {
                            borderColor: accent,
                            bgcolor: isDark ? 'rgba(34,197,94,0.05)' : 'rgba(34,197,94,0.03)',
                          },
                        }}
                      >
                        {logoSrc && (
                          <Box component="img" src={logoSrc} alt={chain.name} sx={{ width: 20, height: 20, borderRadius: '50%' }} />
                        )}
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: isSelected ? 600 : 400, color: isSelected ? accent : (isDark ? '#e1e2eb' : '#171717') }}>
                          {chain.name.replace(/\s*(Sepolia|Testnet|Devnet|Amoy|Goerli)\s*/gi, '').trim()}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {!selectedChainId && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Select a network above to continue
                </Alert>
              )}

              {selectedChainId && (
                <>
                  {/* Token Selector */}
                  <Typography variant="caption" sx={{ fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: '0.15em', display: 'block', mb: 1.5 }}>
                    Select Token
                  </Typography>
                  {tokens.length > 0 && (
                    <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                      <InputLabel>Token</InputLabel>
                      <Select
                        value={selectedToken}
                        label="Token"
                        onChange={(e) => setSelectedToken(e.target.value)}
                      >
                        {tokens.map(token => (
                          <MenuItem key={token.address} value={token.address}>
                            {token.symbol} — {token.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {loading && tokens.length === 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 2 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" sx={{ color: subtleText }}>Loading tokens...</Typography>
                    </Box>
                  )}

                  {/* Amount */}
                  {selectedToken && (
                    <>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: '0.15em', display: 'block', mb: 1.5 }}>
                        Lock Amount
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        value={selectedDenomination}
                        onChange={(e) => setSelectedDenomination(e.target.value)}
                        placeholder={`Min ${minDeposit}`}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', color: subtleText }}>
                              {tokens.find(t => t.address === selectedToken)?.symbol || 'Token'}
                            </Typography>
                          </InputAdornment>,
                          sx: { fontFamily: 'monospace' },
                        }}
                        error={selectedDenomination && parseFloat(selectedDenomination) < minDeposit}
                        helperText={`Minimum: ${minDeposit} ${tokens.find(t => t.address === selectedToken)?.symbol || 'Token'}`}
                        sx={{ mb: 3 }}
                      />
                    </>
                  )}

                  {walletBalance !== null && (
                    <Box sx={{ mb: 2, p: 1.5, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)', border: `1px solid ${borderColor}` }}>
                      <Typography variant="caption" sx={{ color: subtleText }}>
                        Wallet Balance: <strong>{walletBalance.toFixed(4)} {tokens.find(t => t.address === selectedToken)?.symbol || 'ETH'}</strong>
                      </Typography>
                    </Box>
                  )}
                </>
              )}

              {availability && (
                <Alert severity={availability.available ? 'success' : 'warning'} sx={{ mb: 2 }}>
                  {availability.available
                    ? `Ready to lock ${availability.denomination} ${availability.token}`
                    : `Amount too small. Minimum: ${minDeposit} ${availability.token || 'ETH'}`
                  }
                </Alert>
              )}

              <Button
                variant="contained"
                fullWidth
                onClick={handleRequestKey}
                disabled={!selectedChainId || !selectedToken || !selectedDenomination || parseFloat(selectedDenomination) < minDeposit || isNaN(parseFloat(selectedDenomination)) || loading}
                startIcon={loading ? <CircularProgress size={18} /> : <ShoppingCart />}
                sx={{
                  bgcolor: accent,
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  py: 1.5,
                  boxShadow: `0 0 20px rgba(34, 197, 94, 0.2)`,
                  '&:hover': { bgcolor: '#16a34a' },
                  '&.Mui-disabled': { bgcolor: isDark ? '#262626' : '#e5e5e5' },
                }}
              >
                {loading ? 'Processing...' : 'Lock Funds'}
              </Button>
            </CardContent>
          </Card>

          {/* Right — How it works */}
          <Box sx={{ position: 'sticky', top: 80, alignSelf: 'start' }}>
            <Card variant="outlined" sx={{ border: `1px solid ${borderColor}`, borderLeft: `3px solid ${accent}`, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#fff' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: '0.15em', display: 'block', mb: 2 }}>
                  How It Works
                </Typography>
                {[
                  { step: '1', title: 'Select Network', desc: 'Choose the chain to deposit on' },
                  { step: '2', title: 'Enter Amount', desc: `Minimum ${minDeposit} per token` },
                  { step: '3', title: 'Lock Funds', desc: 'Send tokens to the treasury contract' },
                  { step: '4', title: 'Claim Keys', desc: 'Request DKG keyshares from nodes' },
                  { step: '5', title: 'Unlock', desc: 'Reconstruct keys for anonymous withdrawal' },
                ].map((item) => (
                  <Box key={item.step} sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                    <Box sx={{
                      width: 24, height: 24, minWidth: 24,
                      bgcolor: `rgba(34,197,94,0.15)`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.7rem', fontWeight: 700, color: accent,
                    }}>
                      {item.step}
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: isDark ? '#e1e2eb' : '#171717' }}>{item.title}</Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: subtleText }}>{item.desc}</Typography>
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Step 2: Make Deposit */}
      {activeStep === 1 && reservation && (
        <Card variant="outlined" sx={{ border: `1px solid ${borderColor}`, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#fff', maxWidth: 600 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Lock Funds</Typography>

            <Box sx={{ mb: 3, p: 2, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)', border: `1px solid ${borderColor}` }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: '0.15em', display: 'block', mb: 1 }}>
                Lock Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {reservation?.amount} {reservation?.token?.symbol || 'ETH'}
                </Typography>
                <Typography variant="caption" sx={{ color: subtleText }}>
                  Chain: {supportedChains.find(c => c.chainId === selectedChainId)?.name || selectedChainId}
                </Typography>
                <Typography variant="caption" sx={{ color: subtleText, fontFamily: 'monospace', fontSize: '0.65rem' }}>
                  {reservation?.deposit_address || multiTokenTreasuryService.contractAddress}
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              fullWidth
              onClick={handleDeposit}
              startIcon={<AccountBalanceWallet />}
              sx={{
                bgcolor: accent,
                fontWeight: 700,
                py: 1.5,
                '&:hover': { bgcolor: '#16a34a' },
              }}
            >
              Confirm & Lock Funds
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Deposit Complete */}
      {activeStep === 2 && (
        <Card variant="outlined" sx={{ border: `1px solid ${borderColor}`, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#fff', maxWidth: 600 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CheckCircle sx={{ color: accent }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Funds Locked</Typography>
            </Box>

            {txHash && (
              <Box sx={{ mb: 2, p: 2, bgcolor: isDark ? 'rgba(34,197,94,0.06)' : 'rgba(34,197,94,0.04)', border: `1px solid rgba(34,197,94,0.2)` }}>
                <Typography variant="caption" sx={{ color: subtleText, display: 'block', mb: 0.5 }}>Transaction</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>{txHash}</Typography>
                <Button size="small" startIcon={<Launch />} onClick={() => window.open(getExplorerUrl(txHash), '_blank')} sx={{ mt: 1, color: accent }}>
                  View on Explorer
                </Button>
              </Box>
            )}

            <Box sx={{ mb: 2, p: 2, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)', border: `1px solid ${borderColor}` }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {completedDeposit?.amount || reservation?.amount} {completedDeposit?.token?.symbol || reservation?.token?.symbol || 'ETH'}
              </Typography>
              <Typography variant="caption" sx={{ color: subtleText }}>
                {supportedChains.find(c => c.chainId === selectedChainId)?.name || selectedChainId}
              </Typography>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
              Go to <strong>My Keys</strong> to claim your unlock keys.
            </Alert>

            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                onClick={() => navigate('/dashboard')}
                startIcon={<VpnKey />}
                sx={{
                  bgcolor: accent,
                  fontWeight: 700,
                  '&:hover': { bgcolor: '#16a34a' },
                }}
              >
                Go to My Keys
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setActiveStep(0);
                  setReservation(null);
                  setTxHash('');
                  setSelectedDenomination('');
                  setCompletedDeposit(null);
                }}
                sx={{ borderColor: isDark ? '#262626' : '#e5e5e5', color: isDark ? '#e1e2eb' : '#171717' }}
              >
                Lock More
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Deposit Confirmation Dialog */}
        <Dialog open={depositDialog} onClose={() => setDepositDialog(false)} fullScreen={isMobile}>
          <DialogTitle>Confirm Lock</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              You are about to lock {reservation?.amount} {reservation?.token?.symbol || 'ETH'}
              in the treasury contract.
            </Typography>
            <Alert severity="warning">
              Make sure you have enough tokens and gas fees in your wallet.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDepositDialog(false)}>Cancel</Button>
            <Button onClick={executeDeposit} variant="contained">
              Confirm Lock
            </Button>
          </DialogActions>
        </Dialog>

        {/* Deposit Progress Dialog */}
        <Dialog
          open={depositProgressDialog}
          onClose={() => depositStatus === 'confirmed' || depositStatus === 'failed' ? setDepositProgressDialog(false) : null}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            {depositStatus === 'pending' && 'Submitting Transaction...'}
            {depositStatus === 'confirming' && 'Confirming Transaction...'}
            {depositStatus === 'confirmed' && 'Funds Locked!'}
            {depositStatus === 'failed' && 'Transaction Failed'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
              {/* Status Icon */}
              {depositStatus === 'pending' && (
                <CircularProgress size={60} sx={{ mb: 3 }} />
              )}
              {depositStatus === 'confirming' && (
                <HourglassEmpty sx={{ fontSize: 60, color: 'warning.main', mb: 3 }} />
              )}
              {depositStatus === 'confirmed' && (
                <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 3 }} />
              )}
              {depositStatus === 'failed' && (
                <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 3 }} />
              )}

              {/* Status Message */}
              <Typography variant="h6" align="center" gutterBottom>
                {depositStatus === 'pending' && 'Please confirm the transaction in your wallet...'}
                {depositStatus === 'confirming' && 'Waiting for blockchain confirmation...'}
                {depositStatus === 'confirmed' && 'Your deposit has been confirmed!'}
                {depositStatus === 'failed' && 'The transaction could not be completed'}
              </Typography>

              {/* Transaction Hash Display */}
              {txHash && (depositStatus === 'confirming' || depositStatus === 'confirmed') && (
                <Box sx={{ mt: 3, width: '100%' }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Transaction Hash:</strong>
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          wordBreak: 'break-all'
                        }}
                      >
                        {txHash}
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => copyToClipboard(txHash)}
                        startIcon={<ContentCopy />}
                        sx={{ minWidth: 'auto' }}
                      >
                        Copy
                      </Button>
                    </Box>
                  </Alert>

                  {/* Explorer Link */}
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<Launch />}
                    onClick={() => window.open(getExplorerUrl(txHash), '_blank')}
                    sx={{ mt: 1 }}
                  >
                    View on Explorer
                  </Button>
                </Box>
              )}

              {/* Additional Info */}
              {depositStatus === 'confirmed' && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                  Proceeding to next step...
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            {(depositStatus === 'confirmed' || depositStatus === 'failed') && (
              <Button onClick={() => setDepositProgressDialog(false)} variant="contained">
                Close
              </Button>
            )}
          </DialogActions>
        </Dialog>
    </Box>
  );
};

export default Deposit;
